import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  Output,
  untracked,
  ViewChild
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { isEqual } from 'lodash-es';
import {
  AchMethodModel,
  CustomerModel,
  OneTimePaymentMethodModel,
  PaymentMethodFormData,
  PaymentMethodMode,
  PaymentMethodModel
} from '../../../../customer/customer.model';
import { PaymentCollectStore } from '../../../store/payment-collect.store';
import { UserStore } from '../../../../user/store/user.store';
import { BlockUiDirective } from 'shared/directives/block-ui.directive';
import { GridColumnActionType } from '../../../../../shared/components/grid/grid-config';
import { achPaymentMethodActions, paymentMethodActions } from '../../../config/actions';
import { OneTimePaymentComponent } from '../../one-time-payment/one-time-payment.component';
import { ActionsService } from '../../../../customer/services/actions.service';
import { describeAccount, isAchMethod } from './account-card-view';
import { isSavedDebitBlocked } from 'app/shared/utils/debit-card.util';
import { AccountCardComponent, AccountCardItem } from './account-card.component';

type PaymentForm = FormGroup<{
  paymentMode: FormControl<PaymentMethodMode | null>,
  paymentMethod: FormControl<PaymentMethodModel | null>,
  oneTimePaymentMethod: FormControl<OneTimePaymentMethodModel | null>,
}>;

export type { AccountCardItem } from './account-card.component';

/** The two modes in which this step chooses a saved account. The others own their own state. */
const SAVED_ACCOUNT_MODES = [PaymentMethodMode.creditcard, PaymentMethodMode.achpay];

/** Default first; otherwise the store's order (most recently updated first). */
const defaultFirst = (a: AccountCardItem, b: AccountCardItem) =>
  Number(!!b.method.isDefault) - Number(!!a.method.isDefault);

/**
 * Step 2 of Collect Payment: which saved account to charge.
 *
 * Cards and ACH accounts are shown together as cards, and **the rail follows the account**:
 * choosing an ACH account is what puts the payment in ACH mode (`PaymentMethodMode.achpay`),
 * choosing a card puts it in card mode. There is no separate ACH toggle — the old one existed
 * only to swap which half of the list was visible, and let the mode and the selection disagree.
 */
@Component({
  selector: 'app-payment-collect-payment-methods',
  templateUrl: './payment-methods.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PaymentCollectPaymentMethodsComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PaymentCollectPaymentMethodsComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    BlockUiDirective,
    OneTimePaymentComponent,
    AccountCardComponent
  ],
  host: {
    class: 'app-payment-collect-payment-methods'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentCollectPaymentMethodsComponent implements ControlValueAccessor, Validator {
  @ViewChild(OneTimePaymentComponent) oneTimePayment!: OneTimePaymentComponent;

  /** The Collect page. Row actions reach it through `actionHost.parent.parent`, as they do from the grid. */
  @Input() parent!: any;
  @Input() isOneTimePay: boolean = true;
  @Input() isOneTimeCard: boolean = false;

  @Output() oneTimePaymentValidated = new EventEmitter<boolean>();
  @Output() addCard = new EventEmitter<void>();
  @Output() addAchAccount = new EventEmitter<void>();

  readonly store = inject(PaymentCollectStore);
  readonly userStore = inject(UserStore);
  readonly actionsService = inject(ActionsService);
  readonly injector = inject(Injector);
  private readonly cd = inject(ChangeDetectorRef);

  readonly achEnabled = computed(
    () => this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.enableAchPay ?? false
  );

  /** The profile refuses debit cards: saved debit cards are shown but cannot be chosen. */
  readonly blockDebitCards = computed(
    () => this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.blockDebitCards === true
  );

  /** Every account this step may offer: ACH accounts only when the profile takes ACH. */
  readonly $accounts = computed<AccountCardItem[]>(() => {
    const achEnabled = this.achEnabled();
    const blockDebitCards = this.blockDebitCards();
    const now = new Date();
    return this.store.data.paymentMethods()
      .filter((method) => achEnabled || !isAchMethod(method))
      .map((method) => ({
        method,
        view: { ...describeAccount(method, now), notAccepted: isSavedDebitBlocked(method, blockDebitCards) },
      }));
  });

  /** A saved card FTE would refuse to charge (blockDebitCards). */
  isNotAccepted(method: PaymentMethodModel): boolean {
    return isSavedDebitBlocked(method, this.blockDebitCards());
  }

  readonly $cards = computed(() => this.$accounts().filter((item) => item.view.rail === 'CARD').sort(defaultFirst));
  readonly $achAccounts = computed(() => this.$accounts().filter((item) => item.view.rail === 'ACH').sort(defaultFirst));

  /**
   * Passed to the shared row-action handlers as their `component`. They were written for the grid,
   * which exposes the component that hosts it as `.parent` — so `.parent` is this step and
   * `.parent.parent` the Collect page, whose `$allowNext` and customer the handlers use.
   */
  readonly actionHost = { parent: this };

  form: PaymentForm = new FormGroup({
    paymentMode: new FormControl<PaymentMethodMode | null>(PaymentMethodMode.creditcard, [Validators.required]),
    paymentMethod: new FormControl<PaymentMethodModel | null>(null, [
      (control) => {
        const paymentMode = this.form?.get('paymentMode')?.value;
        if (!paymentMode || !SAVED_ACCOUNT_MODES.includes(paymentMode)) {
          return null;
        }
        if (!control.value) {
          return { required: true };
        }

        // Ownership cross-check: the selected token must belong to the currently-selected
        // customer. Guards against a stale selection surviving a customer switch — FTE would
        // otherwise reject the charge with 403 at submit time.
        const currentCustomerId = this.store?.customer()?.id;
        const tokenCustomerId = (control.value as PaymentMethodModel)?.customerId;
        if (currentCustomerId && tokenCustomerId && tokenCustomerId !== currentCustomerId) {
          return { tokenOwnershipMismatch: true };
        }

        if (paymentMode === PaymentMethodMode.achpay) {
          const payment = control.value as AchMethodModel;
          if (!payment.abaRoute || !payment.account) {
            return { invalidAchMethod: true };
          }
        }
        return null;
      }
    ]),
    oneTimePaymentMethod: new FormControl<OneTimePaymentMethodModel | null>(null)
  });

  /** An account the Collect page just created, to select once the reloaded list contains it. */
  private pendingSelectId: string | null = null;

  protected onChange: (payment: PaymentMethodFormData) => void = () => {};
  protected onTouched: () => void = () => {};

  constructor() {
    // Keeps the selection honest against the list. Signal writes are allowed because selecting
    // reaches the store through the Collect form's valueChanges; the reconciliation itself runs
    // untracked so only the customer, the list and the ACH flag re-run it.
    effect(() => {
      const customer = this.store.customer();
      const accounts = this.$accounts().map((item) => item.method);
      const loadedFor = this.store.data.paymentMethodsCustomerId();
      untracked(() => this.reconcileSelection(customer, accounts, loadedFor));
    }, { allowSignalWrites: true });
  }

  get isFlashPay(): boolean {
    return this.form.controls.paymentMode.value === PaymentMethodMode.flashpay;
  }

  isSelected(method: PaymentMethodModel): boolean {
    const selectedId = this.form.controls.paymentMethod.value?.id;
    return !!selectedId && selectedId === method.id;
  }

  /**
   * Retires a selection that belongs to another customer or has left the list (deleted, or ACH
   * switched off), refreshes one whose token was edited, and otherwise preselects the default —
   * or the only account, when there is exactly one.
   */
  private reconcileSelection(
    customer: CustomerModel | null,
    accounts: PaymentMethodModel[],
    loadedFor: string | null
  ): void {
    const mode = this.form.controls.paymentMode.value;
    if (!mode || !SAVED_ACCOUNT_MODES.includes(mode) || !customer?.id) {
      return;
    }

    let current = this.form.controls.paymentMethod.value;
    if (current?.customerId && current.customerId !== customer.id) {
      this.pendingSelectId = null;
      this.deselectPaymentMethod();
      current = null;
    }

    // Until the list is this customer's, it cannot say what they hold — an empty list here is
    // "not loaded yet", not "no accounts". Re-run the ownership check and wait.
    if (loadedFor !== customer.id) {
      this.form.controls.paymentMethod.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (this.pendingSelectId) {
      const added = accounts.find((method) => method.id === this.pendingSelectId);
      if (added) {
        this.pendingSelectId = null;
        this.selectPaymentMethod(added);
        return;
      }
    }

    if (current) {
      const fresh = accounts.find((method) => method.id === current!.id);
      if (fresh) {
        // Spread over the current value so anything added to the selection after it was made
        // (a CVV, say) survives; the token's own fields come from the fresh read.
        const changed = (Object.keys(fresh) as (keyof PaymentMethodModel)[])
          .some((key) => !isEqual(fresh[key], current![key]));
        if (changed) {
          this.selectPaymentMethod({ ...current, ...fresh });
        }
        return;
      }
      this.deselectPaymentMethod();
    }

    const usable = accounts.filter((method) => !this.isNotAccepted(method));
    const pick = usable.find((method) => method.isDefault) ?? (usable.length === 1 ? usable[0] : undefined);
    if (pick) {
      this.selectPaymentMethod(pick);
    }
  }

  validate(): ValidationErrors | null {
    return this.form.valid ? null : { required: true };
  }

  writeValue(payment: PaymentMethodFormData): void {
    this.form.patchValue({
      paymentMode: payment?.paymentMode ?? this.form.controls.paymentMode.value ?? PaymentMethodMode.creditcard,
      paymentMethod: payment?.paymentMethod ?? null,
      oneTimePaymentMethod: payment?.oneTimePaymentMethod as OneTimePaymentMethodModel ?? null
    });
    this.cd.markForCheck();
  }

  registerOnChange(fn: (payment: PaymentMethodFormData) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /** Choosing an account chooses its rail. */
  selectPaymentMethod(method: PaymentMethodModel): void {
    if (this.isNotAccepted(method)) {
      return;
    }
    this.form.patchValue({
      paymentMode: isAchMethod(method) ? PaymentMethodMode.achpay : PaymentMethodMode.creditcard,
      paymentMethod: { ...method }
    });
    this.onChange(this.form.getRawValue());
    this.cd.markForCheck();
  }

  deselectPaymentMethod(): void {
    // With no account chosen there is no rail, so an ACH mode falls back to the card default.
    const mode = this.form.controls.paymentMode.value;
    this.form.patchValue({
      paymentMode: mode === PaymentMethodMode.achpay ? PaymentMethodMode.creditcard : mode,
      paymentMethod: null
    });
    this.onChange(this.form.getRawValue());
    this.cd.markForCheck();
  }

  /** Called by the Collect page after it creates an account, so the new one ends up chosen. */
  selectWhenLoaded(paymentMethodId: string | null | undefined): void {
    this.pendingSelectId = paymentMethodId ?? null;
  }

  setDefaultPaymentMethod(method: PaymentMethodModel): void {
    this.actionsService.handlePaymentSetDefaultAction(method);
  }

  /** The shared row actions' post-edit refresh hook — the grid's host exposes the same name. */
  loadPaymentMethods(): void {
    this.store.reloadPaymentMethods();
  }

  actionsFor(method: PaymentMethodModel): GridColumnActionType<PaymentMethodModel>[] {
    const actions = isAchMethod(method) ? achPaymentMethodActions : paymentMethodActions;
    return actions.filter((action) => !action.canShow || action.canShow(method, this.actionHost, this.injector));
  }

  runAction(action: GridColumnActionType<PaymentMethodModel>, method: PaymentMethodModel): void {
    action.handler(method, this.actionHost, this.injector);
  }

  selectOneTimePaymentMethod(isValid: boolean) {
    // Both `isOneTimePay` and `isOneTimeCard` flows render the same
    // OneTimePaymentComponent — only the persisted payment mode differs.
    if ((this.isOneTimePay || this.isOneTimeCard) && this.oneTimePayment) {
      const oneTimePaymentData = this.oneTimePayment.getFormData();

      const isAch = oneTimePaymentData.paymentType === 'ACH';

      const paymentMethod: PaymentMethodModel = isAch
        ? ({
            tokenType: 'ACH',
            account: oneTimePaymentData.account,
            abaRoute: oneTimePaymentData.abaRoute,
            accountHolderName: oneTimePaymentData.accountHolderName,
            accountHolderFirstName: oneTimePaymentData.accountHolderFirstName,
            accountHolderLastName: oneTimePaymentData.accountHolderLastName,
            accountType: oneTimePaymentData.accountType,
          } as AchMethodModel)
        : {
            account: oneTimePaymentData.account,
            cardHolderName: oneTimePaymentData.cardHolderName,
            expirationDate: oneTimePaymentData.expirationDate,
            cvv: oneTimePaymentData.cvv,
            streetAddress: oneTimePaymentData.streetAddress,
            zipCode: oneTimePaymentData.zipCode,
            bypassCVV: oneTimePaymentData.bypassCVV,
            bypassAVS: oneTimePaymentData.bypassAVS,
            issueBank: oneTimePaymentData.issueBank,
            cardFunding: oneTimePaymentData.cardFunding,
            cardLabel: oneTimePaymentData.cardLabel
          };

      const oneTimePaymentMethod: OneTimePaymentMethodModel = {
        paymentType: oneTimePaymentData.paymentType ?? 'CARD',
        customerName: oneTimePaymentData.customerName,
        customerReferenceNumber: oneTimePaymentData.customerReferenceNumber,
        saveCustomer: oneTimePaymentData.saveCustomer,
        saveToken: oneTimePaymentData.saveToken,
        encryptedPaymentAccount: oneTimePaymentData.encryptedPaymentAccount,
      };

      const targetMode = this.isOneTimeCard ? PaymentMethodMode.onetimecard : PaymentMethodMode.onetimepay;

      this.form.patchValue({
        paymentMode: targetMode,
        paymentMethod,
        oneTimePaymentMethod
      });

      this.oneTimePaymentValidated.emit(isValid);
      this.onChange(this.form.getRawValue());
    }
  }
}
